// This module is deliberately maintained as a byte-identical mirror in
// frontend/gateway/src/ai-integration/publicVocabulary.js and
// frontend/cseditor/src/ai-integration/publicVocabulary.js (the two bundles share no build
// infrastructure). Each frontend's test suite asserts the copies match — edit
// both files together. Same arrangement as plcCapabilityManifest.js.
//
// What the AI may repeat, and what it may never repeat.
//
// The AI answers with whatever we hand it. When a tool passes a backend
// payload through unprojected, the model faithfully quotes our internal
// engineering vocabulary back at a customer: opaque runtime IDs, raw enum
// values the UI deliberately de-jargons, internal capability identifiers, and
// — worst — the release-gate register that names exactly which evidence we
// have not produced for an unreleased feature.
//
// This module is the vocabulary half of the fix. The projection half lives in
// plcPublicProjection.js and in the individual tools. The rule they share:
//
//   FAIL CLOSED, DO NOT PARTIALLY REDACT.
//
// A string that contains an internal term is replaced wholesale by a sentence
// written for an operator, never edited word-by-word. Surgical redaction
// produces "The build advertises signed execution for , manifest v2, and  as
// one exact contract." — which still tells the reader something was removed,
// and reads worse than the plain sentence we could have written instead.
// cmd/publicdocs/main.go takes the same position for the public docs site and
// says why: naming a gap the reader cannot see reads worse than the gap.
//
// This list is deliberately a superset of cmd/publicdocs' internalVocabulary.
// Both guard the same thing — text leaving the building — so a term banned
// from the docs site is banned here too.

// Internal capability, profile, and artifact identifiers.
//
// Shape: two or more lowercase kebab segments followed by a version suffix —
// controlseat-plc-ir-v15, iec-program-pou-v1, controller-project-resource-v1,
// deterministic-program-scheduler-v2, edge-tag-selective-proof-v1,
// fr202-analog-input-v1, local-plc-v1.
//
// The trailing -v<n> is load-bearing. Matching "two or more hyphens" alone also
// catches ordinary operator English — "up-to-date", "point-to-point" — and a
// guard that rejects plain language would push callers to write worse copy to
// get past it.
const CAPABILITY_ID = /\b[a-z][a-z0-9]*(?:-[a-z0-9]+)+-v\d+\b/g;

// Opaque record handles. These are the same class of value as the tag IDs the
// UI is forbidden from showing: meaningful only to the database.
const OPAQUE_ID = /\b(?:edge|gw|ctrl|conn|prog|tnt|sess)_[0-9a-f]{12,}\b/gi;

// Content digests. The fact that a build was verified is useful; sixty-four
// hex characters are not.
const DIGEST = /\b(?:sha256:)?[0-9a-f]{32,}\b/gi;

// Terms that reveal internal process, structure, or source layout. Matched
// case-insensitively as whole phrases.
const INTERNAL_PHRASES = [
  // Release-gate machinery. The single most sensitive category: it discloses
  // unreleased work, the exact evidence we are missing, and that code ships
  // without license clearance or hardware-in-the-loop testing.
  "release gate",
  "release_blocked",
  "release-blocked",
  "release evidence",
  "sbom",
  "hil report",
  "hil evidence",
  "hardware-in-the-loop",
  "soak report",
  "failsafe report",
  "license review",
  "hardware lab",
  "hardware-lab",
  "qualification evidence",
  "not_implemented",
  "host_blocked",

  // Internal documents and planning artifacts.
  "gap g-",
  "gap **g-",
  "docs/plans/",
  "trust roadmap",
  "trust-requirements",
  "roadmap stage",

  // Source layout and host implementation detail.
  "core/",
  "cmd/",
  ".go`",
  "systemd",
  "cgroup",

  // Build and deployment internals.
  "build manifest",
  "build_manifest",
  "deployment digest",
  "deployment_sha256",
  "build_content_sha256",
  "manifest v2",
  "manifest-v2",

  // Raw state and policy field names the UI already translates.
  "in_sync",
  "sync_state",
  "actual_state",
  "desired_state",
  "desired_revision",
  "configuration_only",
  "execution_available",
  "activation_eligible",
  "calibration_required",
  "not_enabled",
  "deployment_floors",
  "run_policy",
  "require_signed",
  "preempt_rt",
  "realtime_priority_privilege",
  "raw_ethernet_privilege",
  "snapshot_stale",
  "plc_artifact_support",
];

const phrasePattern = new RegExp(
  INTERNAL_PHRASES.map((phrase) => phrase.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|"),
  "gi"
);

/**
 * Every internal term found in `text`, for diagnostics and for the guard test
 * that scans tool output and prompt copy. Returns [] for non-strings.
 */
export const findInternalTerms = (text) => {
  if (typeof text !== "string" || text === "") return [];
  const found = new Set();
  for (const pattern of [phrasePattern, CAPABILITY_ID, OPAQUE_ID, DIGEST]) {
    pattern.lastIndex = 0;
    for (const match of text.matchAll(pattern)) found.add(match[0].toLowerCase());
  }
  return [...found];
};

export const hasInternalTerm = (text) => findInternalTerms(text).length > 0;

/**
 * publicSentence is the fail-closed substitution. Return `text` when it is
 * already safe for an operator to read; otherwise return `fallback`, which the
 * caller writes to preserve the *meaning* of what it replaced.
 *
 * Callers must supply a fallback that is true, not merely vague. "This feature
 * is not available on this runtime" is a fair replacement for a blocked
 * release gate. "Something went wrong" is not.
 */
export const publicSentence = (text, fallback) => {
  const value = typeof text === "string" ? text.trim() : "";
  if (!value) return fallback;
  return hasInternalTerm(value) ? fallback : value;
};

// ── Plain-language state ────────────────────────────────────────────────
//
// The gateway UI already owns this translation for connectors
// (connectorStatusLine, humanizeConnectorDiagnostic). Nothing in the AI path
// used it, so the model saw `in_sync` and printed `in_sync`. These maps give
// the AI the same words the UI shows, so the two surfaces cannot describe the
// same device differently.
//
// Values are sentences rather than adjectives on purpose. `sync_state:
// "pending"` invites the model to invent what pending means; "the runtime has
// not confirmed this yet" does not.

const SYNC_STATE = {
  in_sync: "matches what the device is running",
  pending: "sent to the device, which has not confirmed it yet",
  rejected: "refused by the device",
  drifted: "different from what the device is actually running",
  rollback: "being rolled back to the previous version",
  not_downloaded: "not installed on the runtime",
  version_mismatch: "different from the application version on the runtime",
  run_state_mismatch: "in a different run state from the one requested",
  faulted: "reporting an application fault",
  unmanaged: "present on the runtime but not managed by Control Seat",
  command_failed: "unchanged because the most recent application command failed",
  unknown: "unknown — the device has not reported",
};

const RUN_STATE = {
  running: "running",
  stopped: "stopped",
  starting: "starting",
  stopping: "stopping",
  faulted: "faulted",
  degraded: "running with a problem",
  idle: "idle",
  unknown: "unknown",
};

const CAPABILITY_STATUS = {
  available: "available and executable here",
  configuration_only: "can be configured here but not run here",
  planned: "not available",
};

const TRI_STATE = {
  present: "met",
  absent: "not met",
  unknown: "could not be checked on this host",
};

const CHECK_STATUS = {
  pass: "met",
  fail: "not met",
  warning: "met with a caveat",
  unknown: "could not be checked on this host",
};

const lookup = (table, value, fallback) => {
  const key = String(value == null ? "" : value).trim().toLowerCase();
  if (!key) return fallback;
  return table[key] || fallback;
};

export const syncStateLabel = (value) =>
  lookup(SYNC_STATE, value, "in a state the device has not reported clearly");

export const runStateLabel = (value) => lookup(RUN_STATE, value, "in an unreported state");

export const capabilityStatusLabel = (value) => lookup(CAPABILITY_STATUS, value, "not available");

export const triStateLabel = (value) => lookup(TRI_STATE, value, "could not be checked on this host");

export const checkStatusLabel = (value) => lookup(CHECK_STATUS, value, "could not be checked on this host");

/**
 * Human protocol names. The wire uses `ethernet_ip_class1`; an operator calls
 * it EtherNet/IP. Unknown protocols fall back to a de-underscored form rather
 * than being dropped, because silently omitting a protocol from a readiness
 * answer is worse than naming it awkwardly.
 */
const PROTOCOL_NAMES = {
  ethernet_ip_class1: "EtherNet/IP (Class 1 cyclic I/O)",
  ethercat: "EtherCAT",
  profinet_io_controller: "PROFINET (IO controller)",
  profibus_dp: "PROFIBUS DP",
};

export const protocolLabel = (value) => {
  const key = String(value == null ? "" : value).trim().toLowerCase();
  if (!key) return "an unnamed protocol";
  return PROTOCOL_NAMES[key] || key.replace(/_/g, " ");
};

// ── Backstop ────────────────────────────────────────────────────────────
//
// Per-tool projection is the real fix: it knows what a field means and can
// write a true sentence in its place. This backstop cannot do that, so it does
// the one thing that is always safe — replace machine-readable identifiers
// inside free text with a marker.
//
// It only rewrites identifiers *embedded in prose*. A string whose entire
// value is one identifier is data, not narrative: `gateway_id` chains the next
// tool call, and `sha256` is echoed back verbatim by confirm_edge_connector.
// Scrubbing those would break every chained lookup and every confirmation to
// hide a string the model should simply never print — and "never print it" is
// a prose rule, enforced by the tool descriptions, the system prompt, and the
// exposure guard test. Not by corrupting the payload.
//
// So: "Backend <cap-id> failed its check" gets rewritten; "<cap-id>" alone
// does not.
//
// BETA-28 narrowed what counts. Opaque record handles -- runtime, connector,
// rollout and key ids -- are no longer redacted anywhere. Battex engineers
// debug with connector ids and revision numbers daily, and a sentence that
// reads "Runtime [internal identifier withheld] rejected revision 7" hides the
// one fact the reader needed. Content digests and key fingerprints still go,
// because nothing an operator does is helped by sixty-four hex characters and
// the signed-revision model depends on them not circulating. Internal
// capability strings still go: they name our engineering vocabulary, not a
// resource the reader can act on.

const REDACTED = "[internal identifier withheld]";

const wholeValue = (pattern, text) => {
  const anchored = new RegExp(`^(?:${pattern.source})$`, pattern.flags.replace("g", ""));
  return anchored.test(text);
};

const redactText = (text) => {
  const trimmed = text.trim();
  if (trimmed === "" || wholeValue(DIGEST, trimmed) || wholeValue(CAPABILITY_ID, trimmed)) {
    return text;
  }
  return text.replace(DIGEST, REDACTED).replace(CAPABILITY_ID, REDACTED);
};

/**
 * scrubToolResultForModel walks a tool result and redacts identifiers found
 * inside free-text values.
 *
 * Depth is bounded: tool results are shallow by construction, and an unbounded
 * walk over a cyclic payload would hang the turn rather than fail it.
 */
export const scrubToolResultForModel = (value, depth = 0) => {
  // Returning the original value here would leave a cycle intact. The caller
  // serializes tool results immediately afterward, so a cyclic or maliciously
  // deep result must become a finite value rather than merely making this
  // walker stop recursing.
  if (depth > 12) return "[nested value omitted]";
  if (typeof value === "string") return redactText(value);
  if (Array.isArray(value)) return value.map((item) => scrubToolResultForModel(item, depth + 1));
  if (value && typeof value === "object") {
    // Preserve class instances (Error, Date) rather than flattening them into
    // plain objects on the way through.
    if (value.constructor !== Object) return value;
    const out = {};
    for (const [key, item] of Object.entries(value)) {
      out[key] = scrubToolResultForModel(item, depth + 1);
    }
    return out;
  }
  return value;
};
